import React, { useState, useEffect, useContext, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { AppContext } from '../../application/provider';
import * as MemorandumService from '../../axios_services/memorandum.service';
import ArchivoService from '../../axios_services/archivos.service';
import { Paperclip } from 'lucide-react';

const PERFILES_PUEDE_SELECCIONAR_EMISOR = [5, 22];

const ModalMemorandum = ({ isOpen, onClose, memorandum, onRefresh, personalList, emisorList, currentUserId }) => {
    const [state] = useContext(AppContext);
    const perfilId = Number(state.user?.perfil_id);
    const puedeSeleccionarEmisor = PERFILES_PUEDE_SELECCIONAR_EMISOR.includes(perfilId);

    const nombreUsuarioActual = (state.user?.personal || [])
        .find(p => Number(p.usuario_id) === currentUserId)?.nombre_completo_usuario || '';

    const [form, setForm] = useState({
        emisor_id: '',
        codigo: '',
        fecha_registro: '',
        asunto: '',
        colaborador_id: '',
    });
    const [archivo, setArchivo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [wasEdit, setWasEdit] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setWasEdit(!!memorandum);
        }
    }, [isOpen, memorandum]);

    const isEdit = isOpen ? !!memorandum : wasEdit;

    useEffect(() => {
        if (isOpen) {
            if (memorandum) {
                setForm({
                    emisor_id: memorandum.emisor_id || currentUserId || '',
                    codigo: memorandum.codigo || '',
                    fecha_registro: memorandum.fecha_registro ? memorandum.fecha_registro.substring(0, 10) : '',
                    asunto: memorandum.asunto || '',
                    colaborador_id: memorandum.colaborador_id || '',
                });
            } else {
                setForm({
                    emisor_id: currentUserId || '',
                    codigo: '',
                    fecha_registro: '',
                    asunto: '',
                    colaborador_id: '',
                });
            }
            setArchivo(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }, [memorandum, isOpen, currentUserId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert("El archivo adjunto no debe pesar más de 5MB.");
                e.target.value = null;
                setArchivo(null);
                return;
            }
            if (file.type !== "application/pdf") {
                alert("Sólo se admiten archivos en formato PDF.");
                e.target.value = null;
                setArchivo(null);
                return;
            }
            setArchivo(file);
        } else {
            setArchivo(null);
        }
    };

    const handleSubmit = async () => {
        if (!form.emisor_id || !form.codigo || !form.fecha_registro || !form.asunto || !form.colaborador_id) {
            alert('Por favor, complete todos los campos obligatorios.');
            return;
        }
        if (!window.confirm(`¿Está seguro de ${isEdit ? 'actualizar' : 'registrar'} este memorándum?`)) return;

        setLoading(true);
        try {
            let res;
            let memoId = null;
            if (isEdit) {
                res = await MemorandumService.updateMemorandum({ ...form, memorandum_id: memorandum.memorandum_id });
                memoId = memorandum.memorandum_id;
            } else {
                res = await MemorandumService.insertMemorandum(form);
                memoId = res?.data?.memorandum?.memorandum_id || res?.memorandum?.memorandum_id;
            }

            if (archivo && memoId) {
                await ArchivoService.guardarDocumentoMemorandum(archivo, memoId, currentUserId);
            }

            onRefresh();
            onClose();
        } catch (err) {
            alert('Error al guardar el memorándum.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static">
            <ModalHeader className="modal-header">
                <div className="titulo" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <h1 style={{ margin: 0, paddingRight: '20px' }}>{isEdit ? 'ACTUALIZAR MEMORÁNDUM' : 'NUEVO MEMORÁNDUM'}</h1>
                    <Button className="close-btn" onClick={onClose} style={{ flexShrink: 0 }}>X</Button>
                </div>
            </ModalHeader>
            <div className="modal-body-footer">
                <ModalBody className="modal-body">
                    <Form className="fila" onSubmit={e => e.preventDefault()}>

                        <FormGroup style={{ gridColumn: '1 / -1' }}>
                            <Label>Emisor: <span style={{ color: 'red' }}>*</span></Label>
                            {puedeSeleccionarEmisor ? (
                                <Input
                                    className="form-control"
                                    type="select"
                                    name="emisor_id"
                                    value={form.emisor_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione el emisor</option>
                                    {(emisorList || []).map(p => (
                                        <option key={p.usuario_id} value={p.usuario_id}>
                                            {p.nombre_completo_usuario}
                                        </option>
                                    ))}
                                </Input>
                            ) : (
                                <Input
                                    className="form-control"
                                    type="text"
                                    value={nombreUsuarioActual}
                                    disabled
                                />
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>Código: <span style={{ color: 'red' }}>*</span></Label>
                            <Input
                                className="form-control"
                                type="text"
                                name="codigo"
                                value={form.codigo}
                                onChange={handleChange}
                                placeholder="Ingrese el código del memorándum"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Fecha: <span style={{ color: 'red' }}>*</span></Label>
                            <Input
                                className="form-control"
                                type="date"
                                name="fecha_registro"
                                value={form.fecha_registro}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        <FormGroup style={{ gridColumn: '1 / -1' }}>
                            <Label>Asunto: <span style={{ color: 'red' }}>*</span></Label>
                            <Input
                                className="form-control"
                                type="textarea"
                                name="asunto"
                                value={form.asunto}
                                onChange={handleChange}
                                placeholder="Ingrese el asunto del memorándum"
                                rows={3}
                            />
                        </FormGroup>

                        <FormGroup style={{ gridColumn: '1 / -1' }}>
                            <Label>Dirigido a: <span style={{ color: 'red' }}>*</span></Label>
                            <Input
                                className="form-control"
                                type="select"
                                name="colaborador_id"
                                value={form.colaborador_id}
                                onChange={handleChange}
                            >
                                <option value="">Seleccione el colaborador</option>
                                {(personalList || []).map(p => (
                                    <option key={p.usuario_id} value={p.usuario_id}>
                                        {p.nombre_completo_usuario}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup style={{ gridColumn: '1 / -1' }}>
                            <Label>Documento adjunto: <span style={{ color: 'red' }}>*</span></Label>
                            <div className="custom-file-upload border p-2" style={{ display: 'flex', alignItems: 'center', backgroundColor: '#ffffff', borderRadius: '30px' }}>
                                <Button
                                    className="button-custom"
                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                    style={{ display: 'flex', alignItems: 'center', gap: '5px', marginRight: '15px', fontSize: '11px', padding: '4px 10px', borderRadius: '8px' }}
                                >
                                    <Paperclip size={14} />
                                    {archivo ? 'Cambiar PDF' : 'Adjuntar PDF'}
                                </Button>
                                <span className="text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, fontSize: '11px' }}>
                                    {archivo ? archivo.name : 'Ningún archivo seleccionado'}
                                </span>
                                <Input
                                    type="file"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    innerRef={fileInputRef}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </FormGroup>

                        <FormGroup style={{ marginTop: '18px', gridColumn: '1 / -1' }}>
                            <div className="button-container">
                                <Button
                                    className="button-custom"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? 'GUARDANDO...' : isEdit ? 'ACTUALIZAR' : 'REGISTRAR'}
                                </Button>
                            </div>
                        </FormGroup>

                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalMemorandum;
